import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Eye,
  Image as ImageIcon,
  FileText,
  ExternalLink,
  Video,
  Music,
  RotateCcw,
  StickyNote,
  AlertTriangle,
  X,
  Download,
  Maximize2,
  Pin,
  Star,
  UserCheck,
  Tag,
  CalendarClock,
  Ban,
  Loader2,
  Bell,
  BellOff,
  CheckCircle2,
  Wrench,
  ChevronRight,
  Lightbulb,
  ShieldAlert,
  BookOpen,
  Reply,
  Phone,
  PhoneOff,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Copy,
  Bot,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatMessageTime, formatRelativeTime, formatFileSize } from '@/lib/utils/format';
import { formatScheduleDate } from '@/lib/utils/schedule';
import { splitOptOutFooter } from '@/lib/utils/opt-out';
import type { Message, MessageStatus, MessageAttachment } from '@/types/messaging';
import type { AppPreferences } from '@/types/settings';
import type { ConvertedMessage } from '@/modules/tpa/whatsapp/whatsapp-message-converter';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useConversationStore } from '@/stores/conversation-store';
import { useAppStore } from '@/stores/app-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useSettingsStore } from '@/stores/settings-store';
import { getWabaErrorDetail, type WabaErrorDetail } from '@/components/conversations/waba-error-codes';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/* ── Chat View display preferences (Settings › Chat View › Display) ── */

type ChatFontSize = NonNullable<AppPreferences['chatFontSize']>;

/** Body text size for regular message bubbles (`preferences.chatFontSize`). */
const BUBBLE_FONT_SIZE_CLASS: Record<ChatFontSize, string> = {
  small: 'text-xs',
  normal: 'text-sm',
  large: 'text-base',
};

/** Body text size for note bubbles — one step under the regular bubble. */
const NOTE_FONT_SIZE_CLASS: Record<ChatFontSize, string> = {
  small: 'text-xs',
  normal: 'text-[13px]',
  large: 'text-[15px]',
};

/**
 * Message timestamp honouring the Chat View display settings:
 *  - `chatTimeFormat`       → 12h / 24h clock
 *  - `chatTimestampDisplay` → 'hover' fades the time in only while the
 *    message row (`group/msg`) is hovered; the surrounding status icon /
 *    colour wrapper is untouched so the layout never shifts.
 */
function MessageTime({ timestamp, className }: { timestamp: Date | string | number; className?: string }) {
  const timeFormat = useSettingsStore((s) => s.preferences.chatTimeFormat ?? '12h');
  const timestampDisplay = useSettingsStore((s) => s.preferences.chatTimestampDisplay ?? 'always');
  return (
    <span
      data-testid="chat-msg-time"
      className={cn(
        timestampDisplay === 'hover' && 'opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150',
        className,
      )}
    >
      {formatMessageTime(timestamp, timeFormat)}
    </span>
  );
}

/* ── Template Button Icon ──────────────────────────────────────── */

function TemplateButtonIcon({ type }: { type?: string }) {
  switch (type) {
    case 'url': case 'URL':
      return <ExternalLink className="h-3 w-3" />;
    case 'phone': case 'PHONE_NUMBER':
      return <Phone className="h-3 w-3" />;
    case 'copy_code': case 'COPY_CODE':
      return <Copy className="h-3 w-3" />;
    case 'quick_reply': case 'QUICK_REPLY':
      return <Reply className="h-3 w-3" />;
    default:
      return null;
  }
}

/* ── Twilio Error Info — types & fetcher ────────────────────────── */

interface TwilioErrorDetail {
  code: number;
  message: string;
  secondary_message?: string;
  description?: string;
  causes?: string;
  solutions?: string;
  docs?: string;
  log_level?: string;
}

/** Module-level cache for the full Twilio error JSON (fetched once). */
let _twilioErrorDetailCache: Record<number, TwilioErrorDetail> | null = null;

async function fetchTwilioErrorDetail(code: number): Promise<TwilioErrorDetail | null> {
  if (_twilioErrorDetailCache) return _twilioErrorDetailCache[code] || null;
  try {
    const res = await fetch('https://www.twilio.com/docs/api/errors/twilio-error-codes.json');
    if (!res.ok) return null;
    const data: TwilioErrorDetail[] = await res.json();
    const map: Record<number, TwilioErrorDetail> = {};
    for (const item of data) {
      if (item.code) map[item.code] = item;
    }
    _twilioErrorDetailCache = map;
    return map[code] || null;
  } catch {
    return null;
  }
}

/** Parse Twilio's markdown-ish bullet strings into clean arrays */
function parseBullets(raw?: string): string[] {
  if (!raw) return [];
  return raw
    .split('\n')
    .map((l) => l.replace(/^\s*\*\s*/, '').trim())
    .filter(Boolean);
}

/* ── Twilio Error Detail Modal ──────────────────────────────────── */

function TwilioErrorModal({
  open,
  onClose,
  errorCode,
  errorMessage,
  detail,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  errorCode: number;
  errorMessage?: string;
  detail: TwilioErrorDetail | null;
  loading: boolean;
}) {
  const causes = parseBullets(detail?.causes);
  const solutions = parseBullets(detail?.solutions);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md sm:max-w-lg p-0 overflow-hidden gap-0">
        <DialogClose onClose={onClose} />

        {/* Header — error code badge + message */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50/60 dark:from-red-950/40 dark:to-orange-950/20 px-5 pt-5 pb-4 border-b border-red-100 dark:border-red-900/40">
          <DialogHeader className="gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-md bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-2 py-0.5 text-xs font-mono font-semibold">
                <ShieldAlert className="h-3 w-3" />
                {errorCode}
              </span>
              {detail?.log_level && (
                <span className={cn(
                  'text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded',
                  detail.log_level === 'ERROR'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                    : detail.log_level === 'WARNING'
                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                )}>
                  {detail.log_level}
                </span>
              )}
            </div>
            <DialogTitle className="text-base text-red-900 dark:text-red-200 leading-snug">
              {detail?.message || errorMessage || 'Message delivery failed'}
            </DialogTitle>
            {detail?.secondary_message && (
              <p className="text-[13px] text-red-700/70 dark:text-red-300/60 leading-relaxed">
                {detail.secondary_message}
              </p>
            )}
          </DialogHeader>
        </div>

        {/* Body — scrollable content area */}
        <div className="overflow-y-auto px-5 py-4 space-y-4" style={{ maxHeight: '50vh' }}>
          {loading && (
            <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Fetching error details...</span>
            </div>
          )}

          {!loading && !detail && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p>No detailed information found for this error code.</p>
            </div>
          )}

          {/* Description */}
          {detail?.description && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />
                Description
              </h4>
              <p className="text-sm text-foreground/90 leading-relaxed">
                {detail.description}
              </p>
            </div>
          )}

          {/* Causes */}
          {causes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                Possible Causes
              </h4>
              <ul className="space-y-1.5">
                {causes.map((cause, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-400 dark:bg-amber-500 shrink-0" />
                    <span className="leading-relaxed">{cause}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Solutions */}
          {solutions.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Lightbulb className="h-3.5 w-3.5 text-emerald-500" />
                Possible Solutions
              </h4>
              <ul className="space-y-1.5">
                {solutions.map((sol, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 dark:bg-emerald-500 shrink-0" />
                    <span className="leading-relaxed">{sol}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer — Twilio docs link */}
        <div className="border-t border-border px-5 py-3 flex items-center justify-between bg-muted/30">
          <a
            href={`https://www.twilio.com/docs/api/errors/${errorCode}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View full documentation
          </a>
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors font-medium px-3 py-1.5 rounded-md hover:bg-muted"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── WABA Error Detail Modal ───────────────────────────────────── */

function WabaErrorModal({
  open,
  onClose,
  errorCode,
  errorMessage,
  detail,
}: {
  open: boolean;
  onClose: () => void;
  errorCode: number;
  errorMessage?: string;
  detail: WabaErrorDetail | null;
}) {
  const solutions = parseBullets(detail?.solutions);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md sm:max-w-lg p-0 overflow-hidden gap-0">
        <DialogClose onClose={onClose} />

        {/* Header — error code badge + message */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50/60 dark:from-red-950/40 dark:to-orange-950/20 px-5 pt-5 pb-4 border-b border-red-100 dark:border-red-900/40">
          <DialogHeader className="gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-md bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 px-2 py-0.5 text-xs font-mono font-semibold">
                <ShieldAlert className="h-3 w-3" />
                {errorCode}
              </span>
              {detail?.category && (
                <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400">
                  {detail.category}
                </span>
              )}
              {detail?.httpStatus && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-mono">
                  HTTP {detail.httpStatus}
                </span>
              )}
            </div>
            <DialogTitle className="text-base text-red-900 dark:text-red-200 leading-snug">
              {detail?.message || errorMessage || 'Message delivery failed'}
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Body — scrollable content area */}
        <div className="overflow-y-auto px-5 py-4 space-y-4" style={{ maxHeight: '50vh' }}>
          {!detail && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p>No detailed information found for this error code.</p>
            </div>
          )}

          {/* Description */}
          {detail?.description && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5" />
                Description
              </h4>
              <p className="text-sm text-foreground/90 leading-relaxed">
                {detail.description}
              </p>
            </div>
          )}

          {/* Solutions */}
          {solutions.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Lightbulb className="h-3.5 w-3.5 text-emerald-500" />
                Possible Solutions
              </h4>
              <ul className="space-y-1.5">
                {solutions.map((sol, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 dark:bg-emerald-500 shrink-0" />
                    <span className="leading-relaxed">{sol}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer — WhatsApp docs link */}
        <div className="border-t border-border px-5 py-3 flex items-center justify-between bg-muted/30">
          <a
            href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View full documentation
          </a>
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors font-medium px-3 py-1.5 rounded-md hover:bg-muted"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Inline Failed Message Error ────────────────────────────────── */

function FailedMessageError({ errorMessage, errorCode, errorDetails, onRetry, tpaType }: {
  errorMessage?: string;
  errorCode?: number;
  errorDetails?: string;
  onRetry?: () => void;
  tpaType?: string;
}) {
  const isWaba = tpaType === 'whatsapp';
  const [modalOpen, setModalOpen] = useState(false);
  const [twilioDetail, setTwilioDetail] = useState<TwilioErrorDetail | null>(null);
  const [wabaDetail, setWabaDetail] = useState<WabaErrorDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFix = async () => {
    if (!errorCode) return;
    setModalOpen(true);
    if (isWaba) {
      // Synchronous lookup from static map
      if (!wabaDetail) setWabaDetail(getWabaErrorDetail(errorCode));
    } else {
      // Async fetch from Twilio's remote JSON
      if (!twilioDetail && !loading) {
        setLoading(true);
        const info = await fetchTwilioErrorDetail(errorCode);
        setTwilioDetail(info);
        setLoading(false);
      }
    }
  };

  const errorDocsUrl = isWaba
    ? 'https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/'
    : `https://www.twilio.com/docs/api/errors/${errorCode}`;

  return (
    <>
      <div className="mt-1.5 pt-1.5 border-t border-red-200/60 dark:border-red-800/50 text-[11px] text-red-600 dark:text-red-400">
        {/* Error message line */}
        <div className="flex items-start gap-1 min-w-0 mb-1.5">
          <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
          <span className="break-words leading-snug">{errorMessage || 'Failed to send'}</span>
          {errorCode && (
            <a
              href={errorDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 font-mono text-[10px] underline underline-offset-2 decoration-red-300 dark:decoration-red-700 hover:text-red-700 dark:hover:text-red-200 transition-colors"
            >
              #{errorCode}
            </a>
          )}
        </div>

        {/* Error details from WhatsApp status_failed_details */}
        {errorDetails && (
          <p className="text-[10px] text-red-500/80 dark:text-red-300/80 leading-snug mb-1.5 break-words">
            {errorDetails}
          </p>
        )}

        {/* WhatsApp 131026 — surface the most likely cause inline so the
            user doesn't have to click "Fix" to learn the recipient may not
            be on WhatsApp / has blocked the business. */}
        {isWaba && errorCode === 131026 && (
          <p className="text-[10px] text-red-500/90 dark:text-red-300/90 leading-snug mb-1.5 break-words italic">
            This user may not be on WhatsApp or has blocked your business.
          </p>
        )}

        {/* Action buttons — Fix & Retry */}
        <div className="flex items-center gap-1">
          {errorCode && (
            <button
              onClick={handleFix}
              className="inline-flex items-center gap-1 rounded-md bg-red-100/80 dark:bg-red-900/20 hover:bg-red-200/80 dark:hover:bg-red-800/30 text-red-700 dark:text-red-400 px-2 py-0.5 font-medium transition-colors text-[11px]"
            >
              <Wrench className="h-2.5 w-2.5" />
              Fix
              <ChevronRight className="h-2.5 w-2.5 -ml-0.5" />
            </button>
          )}
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1 rounded-md bg-red-100/80 dark:bg-red-900/20 hover:bg-red-200/80 dark:hover:bg-red-800/30 text-red-700 dark:text-red-400 px-2 py-0.5 font-medium transition-colors text-[11px]"
            >
              <RotateCcw className="h-2.5 w-2.5" />
              Retry
            </button>
          )}
        </div>
      </div>

      {/* Error detail modal */}
      {errorCode && isWaba && (
        <WabaErrorModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          errorCode={errorCode}
          errorMessage={errorMessage}
          detail={wabaDetail}
        />
      )}
      {errorCode && !isWaba && (
        <TwilioErrorModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          errorCode={errorCode}
          errorMessage={errorMessage}
          detail={twilioDetail}
          loading={loading}
        />
      )}
    </>
  );
}

/** Render note body with @mentions highlighted as styled inline spans */
function renderNoteBodyWithMentions(body: unknown): React.ReactNode {
  // Guard: body must be a string (Firestore docs can contain non-string values)
  if (typeof body !== 'string') return String(body ?? '');
  // Split by @mention pattern — @word (letters, digits, dots, underscores, hyphens)
  const parts = body.split(/(@[\w.\-]+)/g);
  if (parts.length <= 1) return body;
  return parts.map((part, i) => {
    if (/^@[\w.\-]+$/.test(part)) {
      return (
        <span
          key={i}
          className="inline-block rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1 font-medium"
        >
          {part}
        </span>
      );
    }
    return part;
  });
}

const STATUS_ICONS: Record<MessageStatus, typeof Check> = {
  queued: Clock,
  scheduled: CalendarClock,
  sent: Check,
  delivered: CheckCheck,
  failed: AlertCircle,
  read: Eye,
};

const STATUS_COLORS: Record<MessageStatus, string> = {
  queued: 'text-muted-foreground/60',
  scheduled: 'text-primary/50',
  sent: 'text-muted-foreground/60',
  delivered: 'text-primary/60',
  failed: 'text-destructive/60',
  read: 'text-blue-500/60',
};

interface ChatMessageProps {
  message: Message;
  showTimestamp?: boolean;
  onRetry?: (message: Message) => void;
  onPin?: (messageId: string) => void;
  onStar?: (messageId: string) => void;
  onReply?: (message: Message) => void;
  onCancelScheduled?: (messageId: string) => Promise<{ success: boolean; error?: string }>;
  isPinned?: boolean;
  isStarred?: boolean;
  isLastMessage?: boolean;
  isNew?: boolean;
  /** All messages in the thread — used to find the quoted message for reply display */
  allMessages?: Message[];
  /** Previous message in the thread — used to suppress repeated author info */
  previousMessage?: Message;
}

export function ChatMessage({ message, showTimestamp = true, onRetry, onPin, onStar, onReply, onCancelScheduled, isPinned, isStarred, isLastMessage, isNew, allMessages, previousMessage }: ChatMessageProps) {
  const isOutbound = message.direction === 'outbound';
  const isNote = message.isNote;
  const isSystemMsg = message.isSystemMessage;
  const isPinnedNote = message.noteType?.includes('--note--pinned');
  const isReminderNote = message.noteType?.includes('--reminder');
  const isScheduled = message.status === 'scheduled';
  const isCancelled = message.metadata?.rawStatus === 'canceled' || !!(message.metadata?.cancelledAt);
  const isFailed = message.status === 'failed' && !isCancelled;
  const isWhatsApp = String(message.channel || '').toUpperCase() === 'WHATSAPP';
  const StatusIcon = STATUS_ICONS[message.status] || Check;
  const numMedia = parseInt(String(message.metadata?.numMedia || '0'), 10);
  const hasMedia = (message.attachments && message.attachments.length > 0) || !!message.mediaUrl || numMedia > 0;
  const errorCode = message.metadata?.errorCode as number | undefined;
  const errorMessage = message.metadata?.errorMessage as string | undefined;
  const errorDetails = message.metadata?.errorDetails as string | undefined;
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);

  // Settings › Chat View › Display. Fine-grained selectors so a bubble only
  // re-renders when the value it reads actually changes.
  const chatFontSize = useSettingsStore((s) => s.preferences.chatFontSize ?? 'normal');
  const bubbleFontClass = BUBBLE_FONT_SIZE_CLASS[chatFontSize] ?? BUBBLE_FONT_SIZE_CLASS.normal;
  const noteFontClass = NOTE_FONT_SIZE_CLASS[chatFontSize] ?? NOTE_FONT_SIZE_CLASS.normal;
  // Delivery Reports toggle — hides the sent/delivered/read tick next to the
  // timestamp. Failure details (red bubble + Fix/Retry) are error info, not a
  // delivery report, so they stay visible regardless.
  const showDeliveryReports = useSettingsStore((s) => s.preferences.showDeliveryReports !== false);
  const showStatusIcon = isOutbound && showDeliveryReports;

  // Author display for outbound messages — show only on first message of a consecutive streak
  const outboundAuthor = isOutbound ? (message as ConvertedMessage).author : undefined;
  const showOutboundAuthor = (() => {
    if (!outboundAuthor?.name) return false;
    const prevAuthor = (previousMessage as ConvertedMessage | undefined)?.author;
    if (
      previousMessage?.direction === 'outbound' &&
      !previousMessage.isNote &&
      !previousMessage.isSystemMessage &&
      prevAuthor
    ) {
      // Prefer uid match, fall back to email or name since Twilio lazy-loaded
      // authors don't always include a uid field on the stored message doc.
      const sameAuthor =
        (prevAuthor.uid && outboundAuthor.uid && prevAuthor.uid === outboundAuthor.uid) ||
        (prevAuthor.email && outboundAuthor.email && prevAuthor.email === outboundAuthor.email) ||
        (prevAuthor.name && outboundAuthor.name && prevAuthor.name === outboundAuthor.name);
      if (sameAuthor) return false;
    }
    return true;
  })();

  // Viewport-based media resolution: trigger resolveMessageMedia when this
  // message enters the viewport and has unresolved media (e.g. Twilio media list URIs).
  const msgRef = useRef<HTMLDivElement>(null);
  const needsMediaResolve = !!message.metadata?.needsMediaResolve;
  const resolveMessageMedia = useConversationStore((s) => s.resolveMessageMedia);

  useEffect(() => {
    if (!needsMediaResolve || !msgRef.current) return;
    const el = msgRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          resolveMessageMedia(message.id);
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [needsMediaResolve, message.id, resolveMessageMedia]);

  // Viewport-based author lookup: for outbound messages that don't yet have
  // author info (Twilio/non-WhatsApp TPAs), request it lazily when the bubble
  // enters the viewport. The store debounces + batches these requests into
  // chunks of 30 via a single Firestore 'in' query. Results are cached for
  // the session so scrolling back doesn't refetch.
  const requestMessageAuthor = useConversationStore((s) => s.requestMessageAuthor);
  const needsAuthorFetch = isOutbound && !outboundAuthor && !isSystemMsg && !isNote;
  useEffect(() => {
    if (!needsAuthorFetch || !msgRef.current) return;
    const el = msgRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestMessageAuthor(message.id);
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [needsAuthorFetch, message.id, requestMessageAuthor]);

  // Check if note author is the current user → append "(You)"
  const currentUser = useAuthStore((s) => s.user);
  const effectiveUid = getEffectiveUid();
  const isAuthorCurrentUser = !!(
    message.noteAuthor &&
    currentUser &&
    (message.noteAuthor.uid === effectiveUid || message.noteAuthor.email === currentUser.email)
  );

  // System messages render as a centered inline pill
  if (isSystemMsg) {
    return <SystemMessage id={`msg-${message.id}`} message={message} showTimestamp={showTimestamp} />;
  }

  // Call events render as sided bubbles with call icon
  const isCallEvent = !!message.metadata?.isCallEvent;
  if (isCallEvent) {
    return <CallEventBubble id={`msg-${message.id}`} message={message} showTimestamp={showTimestamp} previousMessage={previousMessage} />;
  }

  // If message has no body, no media, isn't a note, and isn't failed — show a minimal indicator
  const hasContent = message.body || hasMedia || message.status === 'failed' || isNote;
  if (!hasContent) {
    return (
      <div
        className={cn('flex w-full group/msg', isOutbound ? 'justify-end' : 'justify-start')}
      >
        <div
          className={cn(
            'relative max-w-[75%] rounded-2xl px-4 py-2',
            bubbleFontClass,
            isOutbound
              ? 'bg-primary/50 text-primary-foreground/60 rounded-br-md'
              : 'bg-muted/50 text-muted-foreground rounded-bl-md'
          )}
        >
          <span className="text-[10px] italic">Empty message</span>
          {showTimestamp && (
            <div className={cn('flex items-center gap-1 mt-1', isOutbound ? 'justify-end' : 'justify-start')}>
              <span className={cn('text-[11px]', isOutbound ? 'text-primary-foreground/60' : 'text-muted-foreground')}>
                <MessageTime timestamp={message.timestamp} />
              </span>
              {showStatusIcon && (
                <StatusIcon data-testid="chat-msg-status" className={cn('h-3 w-3', isOutbound ? 'text-primary-foreground/60' : STATUS_COLORS[message.status])} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Notes render in a special layout — centered with author header above the body
  if (isNote) {
    return (
      <div id={`msg-${message.id}`} className="flex flex-col w-full items-center group/msg py-0.5">
        <div className="flex items-center gap-1 max-w-[60%] min-w-[35%]">
          <div className={cn(
            'relative w-full rounded-lg bg-[#fff6d6] dark:bg-[#56583f]/60 text-[#555556] dark:text-amber-100 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] px-4 py-3 leading-relaxed',
            noteFontClass,
          )}>
            {/* Note header — icon, then avatar + author name + action */}
            <div className="flex items-center gap-1.5 mb-1.5 text-[11px] text-[#c4650f] dark:text-[#e1a46d]">
              {isPinnedNote ? (
                <Pin className="h-3 w-3 shrink-0" />
              ) : (
                <StickyNote className="h-3 w-3 shrink-0" />
              )}
              <NoteAuthorAvatar author={message.noteAuthor} />
              <span>
                {message.noteAuthor?.name ? (
                  <>
                    <span className="font-semibold">{message.noteAuthor.name}{isAuthorCurrentUser ? ' (You)' : ''}</span>
                    {isPinnedNote ? ' pinned a message' : ' added a note'}
                  </>
                ) : (
                  isPinnedNote ? 'Pinned message' : 'Note'
                )}
              </span>
            </div>

            {/* Note media — same renderer as message bubbles, so images preview
                and other files get the download card */}
            {!!hasMedia && <MediaAttachments message={message} isOutbound={false} />}

            {/* Note body — with @mention highlighting */}
            {message.body && (
              <p className="whitespace-pre-wrap break-words">
                {renderNoteBodyWithMentions(message.body)}
              </p>
            )}

            {/* Timestamp */}
            {showTimestamp && (
              <div className="flex items-center justify-end mt-1.5">
                <span className="text-[10px] text-[#b8860b]/60 dark:text-amber-400/50">
                  <MessageTime timestamp={message.timestamp} />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={`msg-${message.id}`}
      ref={msgRef}
      className={cn(
        'flex flex-col w-full group/msg transition-colors duration-700',
        isOutbound ? 'items-end' : 'items-start',
        '[&.pinned-highlight]:bg-amber-100/60 dark:[&.pinned-highlight]:bg-amber-900/20 [&.pinned-highlight]:rounded-lg',
        // `isNew` previously applied a blue background tint via
        // `.animate-new-message`. We now flag new messages with a small
        // dot rendered next to the bubble (see below) so the row isn't
        // visually differentiated by background colour.
        isLastMessage && 'animate-slide-up'
      )}
    >
      {/* Author name label — outside bubble, above the row */}
      {showOutboundAuthor && outboundAuthor && (
        <span className="text-[11px] text-muted-foreground/70 font-medium mb-0.5 mr-1 truncate max-w-[200px]">
          {outboundAuthor.name}{outboundAuthor.uid === getEffectiveUid() ? ' (You)' : ''}
        </span>
      )}

      {/* Message row — avatar + bubble + hover actions */}
      <div className={cn(
        'flex gap-1.5 max-w-full sm:max-w-[80%] min-w-0',
        isOutbound ? 'flex-row-reverse items-start' : 'flex-row items-center'
      )}>

      {/* Author avatar — outside the bubble, only on first message of a streak */}
      {isOutbound && outboundAuthor && (
        showOutboundAuthor
          ? <div className="pt-0.5 shrink-0"><NoteAuthorAvatar author={outboundAuthor} size="sm" variant="note" /></div>
          : <div className="w-4 shrink-0" />
      )}

      <div
        data-testid="chat-message-bubble"
        className={cn(
          'relative max-w-full min-w-0 overflow-hidden rounded-2xl px-4 py-2 leading-relaxed shrink',
          bubbleFontClass,
          isCancelled
            ? 'bg-muted dark:bg-muted/70 text-muted-foreground border border-border border-dashed line-through decoration-muted-foreground/50 rounded-br-md'
            : (message.status === 'failed' || message.status === 'undelivered' || errorCode)
            ? 'bg-red-100 dark:bg-red-950 text-red-900 dark:text-red-300 border border-red-200 dark:border-red-800/40 rounded-br-md'
            : isScheduled
            ? 'bg-primary/15 dark:bg-primary/20 text-foreground border border-primary/25 dark:border-primary/30 border-dashed rounded-br-md'
            : isOutbound && isWhatsApp
            ? 'bg-gradient-to-br from-[#d9fdd3] to-[#c8edc2] text-[#111b21] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] dark:from-[#005c4b] dark:to-[#004a3d] dark:text-[#e9edef] dark:shadow-[0_1px_0.5px_rgba(0,0,0,0.25)] rounded-br-md'
            : isOutbound
            ? 'bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-br-md'
            : 'bg-card text-foreground rounded-bl-md border border-border/50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
        )}
      >
          {/* New-message dot — inside the bubble, top-right corner. Pinned
              with absolute positioning so it doesn't reflow the message
              content. Uses bg-blue-500 (the prior `.animate-new-message`
              hue) so it stays visible on every bubble variant — light
              card, primary blue, WhatsApp green — without per-variant
              colour switching. */}
          {isNew && (
            <span
              className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-blue-500 shadow-[0_0_0_2px_rgba(255,255,255,0.4)] z-10"
              aria-label="New message"
            />
          )}

          {/* Quoted reply — shows the message this is replying to */}
          {message.replyToMessageId && (() => {
            const quoted = allMessages?.find((m) => m.id === message.replyToMessageId);
            const quotedText = quoted?.body
              ? (quoted.body.length > 100 ? quoted.body.slice(0, 100) + '…' : quoted.body)
              : 'Original message';
            const quotedIsOutbound = quoted?.direction === 'outbound';
            return (
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById(`msg-${message.replyToMessageId}`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.classList.add('pinned-highlight');
                    setTimeout(() => el.classList.remove('pinned-highlight'), 2000);
                  }
                }}
                className={cn(
                  'w-full text-left rounded-lg px-3 py-1.5 mb-1.5 text-xs leading-snug cursor-pointer transition-colors border-l-3',
                  isOutbound && isWhatsApp
                    ? 'bg-[#111b21]/8 border-[#111b21]/25 hover:bg-[#111b21]/12 dark:bg-[#e9edef]/8 dark:border-[#e9edef]/20 dark:hover:bg-[#e9edef]/12'
                    : isOutbound
                    ? 'bg-primary-foreground/10 border-primary-foreground/40 hover:bg-primary-foreground/15'
                    : 'bg-muted/60 dark:bg-muted/40 border-primary/50 hover:bg-muted/80'
                )}
              >
                <span className={cn(
                  'block text-[10px] font-semibold mb-0.5',
                  isOutbound && isWhatsApp ? 'text-[#111b21]/70 dark:text-[#e9edef]/70' : isOutbound ? 'text-primary-foreground/70' : 'text-primary/80'
                )}>
                  {quotedIsOutbound ? 'You' : (quoted?.from?.replace('whatsapp:', '') || 'Contact')}
                </span>
                <span className={cn(
                  'block truncate',
                  isOutbound && isWhatsApp ? 'text-[#667781] dark:text-[#8696a0]' : isOutbound ? 'text-primary-foreground/60' : 'text-muted-foreground'
                )}>
                  {quoted?.attachments?.length ? '📎 Media' : ''}{quoted?.attachments?.length && quoted?.body ? ' · ' : ''}{quotedText}
                </span>
              </button>
            );
          })()}

          {/* Media attachments */}
          {!!hasMedia && <MediaAttachments message={message} isOutbound={isOutbound} />}

          {/* Template indicator */}
          {!!message.metadata?.isTemplate && (
            <div className={cn(
              'flex items-center gap-1 text-[10px] font-medium mb-1 pb-1 border-b',
              isFailed
                ? 'text-red-700 dark:text-red-300 border-red-200/70 dark:border-red-800/60'
                : isOutbound && isWhatsApp
                ? 'text-[#667781] border-[#111b21]/10'
                : isOutbound
                ? 'text-primary-foreground/60 border-primary-foreground/15'
                : 'text-muted-foreground border-border/50'
            )}>
              <FileText className="h-3 w-3 shrink-0" />
              <span>Template message</span>
            </div>
          )}

          {/* Message body with timestamp floated to bottom-right */}
          {message.body ? (
            <div className="relative">
              <p className="whitespace-pre-wrap break-words">
                {(() => {
                  /* Invisible spacer to reserve room for the timestamp (+ status tick) */
                  const timestampSpacer = showTimestamp && !isFailed && (
                    <span className={cn(
                      'inline-block align-bottom',
                      showStatusIcon ? 'w-[4.5rem]' : 'w-[3.5rem]'
                    )} />
                  );
                  // KAN-155: the opt-out footer is part of the text that was sent;
                  // here it is shown as a quiet second line, like a template footer.
                  const { text, footer } = isOutbound
                    ? splitOptOutFooter(message.body)
                    : { text: message.body, footer: null };
                  if (!footer) return <>{message.body}{timestampSpacer}</>;
                  return (
                    <>
                      {text}
                      {/* Dimmed, not recoloured: it follows whatever colour the bubble uses. */}
                      <span
                        data-testid="chat-msg-optout-footer"
                        className={cn('block text-[11px] leading-snug opacity-60', text && 'mt-1')}
                      >
                        {footer}
                        {timestampSpacer}
                      </span>
                    </>
                  );
                })()}
              </p>
              {/* Timestamp pinned to bottom-right */}
              {showTimestamp && !isFailed && (
                <span
                  className={cn(
                    'absolute bottom-0 right-0 inline-flex items-center gap-1 whitespace-nowrap text-[11px]',
                    isCancelled
                      ? 'text-muted-foreground/60'
                      : isScheduled
                      ? 'text-primary/40 dark:text-primary/50'
                      : isOutbound && isWhatsApp
                      ? 'text-[#667781] dark:text-[#8696a0]'
                      : isOutbound
                      ? 'text-primary-foreground/60'
                      : 'text-muted-foreground/50'
                  )}
                >
                  <MessageTime timestamp={message.timestamp} />
                  {showStatusIcon && (
                    <StatusIcon data-testid="chat-msg-status"
                      className={cn(
                        isWhatsApp && (message.status === 'delivered' || message.status === 'read') ? 'h-3.5 w-3.5' : 'h-3 w-3',
                        isCancelled
                          ? 'text-muted-foreground/50'
                          : isScheduled
                          ? 'text-primary/50 dark:text-primary/60'
                          : isWhatsApp
                          ? message.status === 'read'
                            ? 'text-[#53bdeb]'
                            : 'text-[#667781] dark:text-[#8696a0]'
                          : 'text-primary-foreground/60'
                      )}
                    />
                  )}
                </span>
              )}
            </div>
          ) : !hasMedia ? (
            <span className="text-[10px] italic">Empty message</span>
          ) : null}

          {/* Failed text messages: render timestamp on its own row */}
          {message.body && isFailed && showTimestamp && (
            <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-red-500">
              <MessageTime timestamp={message.timestamp} />
              {showStatusIcon && <StatusIcon data-testid="chat-msg-status" className="h-3 w-3 text-red-500" />}
            </div>
          )}

          {/* Template buttons */}
          {!!message.metadata?.isTemplate && (() => {
            const msg = message as ConvertedMessage;
            if (!msg.buttons || msg.buttons.length === 0) return null;
            return (
              <div className={cn(
                'mt-2 pt-1.5 border-t space-y-1',
                isFailed
                  ? 'border-red-200/70 dark:border-red-800/60'
                  : isOutbound && isWhatsApp
                  ? 'border-[#111b21]/10'
                  : isOutbound
                  ? 'border-primary-foreground/15'
                  : 'border-border/50'
              )}>
                {msg.buttons.map((btn, i) => (
                  <div
                    key={i}
                    className={cn(
                      'flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
                      isFailed
                        ? 'bg-red-50/70 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                        : isOutbound && isWhatsApp
                        ? 'text-[#027eb5]'
                        : isOutbound
                        ? 'text-primary-foreground/70'
                        : 'text-primary'
                    )}
                  >
                    <TemplateButtonIcon type={btn.type} />
                    {btn.text}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Timestamp below media for media-only messages (no body text) */}
          {!message.body && hasMedia && showTimestamp && (
            <div className={cn('flex items-center gap-1 mt-1', isOutbound ? 'justify-end' : 'justify-end')}>
              <span
                className={cn(
                  'text-[11px]',
                  isFailed
                    ? 'text-red-500'
                    : isOutbound
                    ? 'text-primary-foreground/60'
                    : 'text-muted-foreground'
                )}
              >
                <MessageTime timestamp={message.timestamp} />
              </span>
              {showStatusIcon && (
                <StatusIcon data-testid="chat-msg-status"
                  className={cn(
                    isWhatsApp && (message.status === 'delivered' || message.status === 'read') ? 'h-3.5 w-3.5' : 'h-3 w-3',
                    isFailed
                      ? 'text-red-500'
                      : isWhatsApp
                      ? message.status === 'read'
                        ? 'text-[#53bdeb]'
                        : 'text-[#667781] dark:text-[#8696a0]'
                      : isOutbound
                      ? 'text-primary-foreground/60'
                      : STATUS_COLORS[message.status]
                  )}
                />
              )}
            </div>
          )}

          {/* Cancelled message indicator */}
          {isCancelled && (
            <div className="mt-1.5 pt-1.5 border-t border-border/40 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Ban className="h-3 w-3 shrink-0" />
              <span className="font-medium">Cancelled</span>
            </div>
          )}

          {/* Failed message error details (not cancelled) */}
          {isFailed && (
            <FailedMessageError
              errorMessage={errorMessage}
              errorCode={errorCode}
              errorDetails={errorDetails}
              onRetry={onRetry ? () => onRetry(message) : undefined}
              tpaType={appCodeTPA ?? undefined}
            />
          )}

          {/* Scheduled message indicator with cancel button */}
          {isScheduled && (
            <ScheduledIndicator
              message={message}
              onCancel={onCancelScheduled}
            />
          )}


      </div>

      {/* Hover action icons — shown at the side of the bubble */}
      <div className="flex items-center gap-0.5 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150 shrink-0">
        {onReply && !isNote && !isSystemMsg && !isCancelled && (
          <button
            onClick={() => onReply(message)}
            className="rounded-full p-1 transition-colors text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted"
            title="Reply"
          >
            <Reply className="h-3.5 w-3.5" />
          </button>
        )}
        {onPin && (
          <button
            onClick={() => onPin(message.id)}
            className={cn(
              'rounded-full p-1 transition-colors',
              isPinned
                ? 'text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                : 'text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted'
            )}
            title={isPinned ? 'Unpin message' : 'Pin message'}
          >
            <Pin className={cn('h-3.5 w-3.5', isPinned && 'fill-current')} />
          </button>
        )}
        {onStar && (
          <button
            onClick={() => onStar(message.id)}
            className={cn(
              'rounded-full p-1 transition-colors',
              isStarred
                ? 'text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                : 'text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted'
            )}
            title={isStarred ? 'Unstar message' : 'Star message'}
          >
            <Star className={cn('h-3.5 w-3.5', isStarred && 'fill-current')} />
          </button>
        )}
      </div>
      </div>

    </div>
  );
}

/** Media attachments renderer — handles resolved and unresolved media */
function MediaAttachments({ message, isOutbound }: { message: Message; isOutbound: boolean }) {
  const resolvedAttachments = message.attachments?.filter((a) => a.url) || [];
  const unresolvedCount = (message.attachments?.filter((a) => !a.url).length || 0);
  const hasResolvedMedia = resolvedAttachments.length > 0;
  const hasDirectUrl = message.mediaUrl && !message.mediaUrl.includes('/Media');
  // Settings › Chat View › Media Display — 'link' renders a compact download
  // card instead of an inline preview, so the loading skeleton is compact too.
  const linkOnly = useSettingsStore((s) => s.preferences.mediaDisplayMode === 'link');

  if (hasResolvedMedia) {
    return (
      <div className="mb-2 space-y-1.5">
        {resolvedAttachments.map((attachment, idx) => (
          <SingleAttachment
            key={idx}
            attachment={attachment}
            isOutbound={isOutbound}
          />
        ))}
      </div>
    );
  }

  // Direct media URL (not a Twilio media list)
  if (hasDirectUrl && message.mediaUrl) {
    return (
      <div className="mb-2">
        <SingleAttachment
          attachment={{ url: message.mediaUrl, type: 'image' }}
          isOutbound={isOutbound}
        />
      </div>
    );
  }

  // Unresolved media — show skeleton placeholder
  const numMedia = unresolvedCount || parseInt(String(message.metadata?.numMedia || '0'), 10);
  if (numMedia > 0) {
    return (
      <div className="mb-2 space-y-1.5">
        {Array.from({ length: Math.min(numMedia, 3) }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'rounded-lg overflow-hidden animate-pulse',
              isOutbound ? 'bg-primary-foreground/15' : 'bg-muted'
            )}
          >
            <div className={cn('flex items-center justify-center', linkOnly ? 'h-9 w-44' : 'h-40 w-56')}>
              <ImageIcon className={cn('opacity-30', linkOnly ? 'h-4 w-4' : 'h-6 w-6')} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}

/** Lightbox overlay for full-size image/video with download */
function MediaLightbox({
  url,
  type,
  contentType,
  onClose,
}: {
  url: string;
  type: 'image' | 'video';
  contentType?: string;
  onClose: () => void;
}) {
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop()?.split('?')[0] || (type === 'video' ? 'video' : 'image');
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Top bar with actions */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-end gap-2 p-3 z-10">
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 text-xs font-medium transition-colors backdrop-blur-sm"
          title="Download"
        >
          <Download className="h-4 w-4" />
          <span>Download</span>
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 text-xs font-medium transition-colors backdrop-blur-sm"
          title="Open in new tab"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white h-8 w-8 transition-colors backdrop-blur-sm"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Media content */}
      <div className="max-w-[90vw] max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {type === 'image' ? (
          <img
            src={url}
            alt="Full size media"
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
        ) : (
          <video
            controls
            autoPlay
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
            playsInline
          >
            <source src={url} type={contentType || 'video/mp4'} />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * Filename carried in a media URL's last path segment, or '' when there isn't
 * a usable one. Firebase Storage URLs percent-encode the path
 * (t%2Fmyfile_123.pdf → myfile_123.pdf); Twilio media URLs end in an MExxx
 * SID, which is not a name.
 */
function attachmentFileNameFromUrl(url?: string): string {
  if (!url || url.startsWith('blob:')) return '';
  const rawName = url.split('/').pop()?.split('?')[0] || '';
  if (!rawName) return '';
  let decoded = rawName;
  try { decoded = decodeURIComponent(rawName); } catch { /* keep raw */ }
  // Decoding a Firebase URL yields the full object path ("t/photo_1786….jpg")
  // — keep the leaf, and drop the `_<ms-timestamp>` uploadAttachment appends.
  const leaf = decoded.split('/').pop() || '';
  if (/^ME[a-f0-9]{32}$/i.test(leaf)) return '';
  return leaf.replace(/_\d{13}(\.[^.]+)?$/, '$1');
}

/** Firebase Storage `t/` temp-upload URL (uploadAttachment):
 *  https://firebasestorage.googleapis.com/v0/b/<bucket>/o/t%2F<file>?alt=media */
function isTempUploadUrl(url: string): boolean {
  return /firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/t(?:%2F|\/)/i.test(url);
}

/** Short format label from a MIME subtype or file extension ("jpeg" → JPEG, "mpeg" audio → MP3). */
function mediaFormatLabel(subtypeOrExt: string, kind: 'image' | 'video' | 'audio'): string {
  const s = subtypeOrExt.trim().toLowerCase();
  if (!s) return '';
  if (s === 'jpg' || s === 'jpeg') return 'JPEG';
  if (kind === 'audio' && (s === 'mpeg' || s === 'mp3')) return 'MP3';
  if (s === 'x-m4a' || s === 'm4a') return 'M4A';
  if (s === 'x-wav' || s === 'wav') return 'WAV';
  if (s === 'quicktime' || s === 'mov') return 'MOV';
  if (s === 'svg+xml') return 'SVG';
  return s.toUpperCase();
}

/**
 * Save a media URL to disk. Fetches to a blob first so the browser shows a real
 * "save file" (a plain `download` attribute is ignored cross-origin). Media the
 * page can't fetch — Twilio URLs behind basic auth, buckets without CORS —
 * falls back to opening in a new tab so the user can still save it from there.
 */
async function downloadAttachment(url: string, filename: string): Promise<void> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const MEDIA_LINK_KIND: Record<'image' | 'video' | 'audio', { Icon: typeof ImageIcon; label: string }> = {
  image: { Icon: ImageIcon, label: 'Image' },
  video: { Icon: Video, label: 'Video' },
  audio: { Icon: Music, label: 'Audio' },
};

/**
 * "Download Link Only" media card (Settings › Chat View › Media Display).
 * Replaces the inline image / video / audio player with a compact row:
 * type tile · name + format/size · Download. The name opens the media in a new
 * tab. Colours derive from `currentColor` (`bg-current/…`) so the same card
 * sits correctly on every bubble — primary blue, WhatsApp green, inbound
 * card, note yellow — without per-variant styling.
 */
function MediaLinkCard({ attachment, kind, isOutbound }: { attachment: MessageAttachment; kind: 'image' | 'video' | 'audio'; isOutbound: boolean }) {
  const [downloading, setDownloading] = useState(false);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const { Icon, label } = MEDIA_LINK_KIND[kind];
  const url = attachment.url;

  // Outbound media goes through our own hosting first: a local blob: preview,
  // then the `t/` temp upload. On Twilio the send flow deletes that upload as
  // soon as the provider fetched it (chat-input handleSend), and Twilio's API
  // keeps echoing the message without media for a while, so the bubble points
  // at a dead URL until a later refresh swaps in the Twilio-hosted media. In
  // that window offer no link/download and show the same generic title the
  // resolved card will have, so nothing but the actions changes. Other TPAs
  // keep the `t/` object as the message's permanent media link; notes and
  // inbound media are never ours to delete.
  const isLocalPreview = url.startsWith('blob:');
  const isDeadTempUpload = isOutbound && appCodeTPA === 'twilio' && isTempUploadUrl(url);
  const canOpen = !isDeadTempUpload;
  const canDownload = !isLocalPreview && !isDeadTempUpload;

  const fileName = isDeadTempUpload ? '' : (attachment.name || attachmentFileNameFromUrl(url));
  // Format: MIME subtype ("image/jpeg; …" → JPEG), else the file extension.
  const subtype = attachment.contentType?.split('/')[1]?.split(';')[0] || '';
  const nameForExt = attachment.name || attachmentFileNameFromUrl(url);
  const ext = nameForExt.includes('.') ? nameForExt.slice(nameForExt.lastIndexOf('.') + 1) : '';
  const format = mediaFormatLabel(subtype || ext, kind);
  const meta = [format, attachment.size ? formatFileSize(attachment.size) : ''].filter(Boolean).join(' · ');
  const title = fileName || `${label} attachment`;
  const subtitle = meta || (canOpen ? 'Open to view or download' : '');
  const downloadName = fileName || `${label.toLowerCase()}${ext ? `.${ext.toLowerCase()}` : ''}`;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadAttachment(attachment.url, downloadName);
    } finally {
      setDownloading(false);
    }
  };

  const body = (
    <>
      <span className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-current/10 transition-colors',
        canOpen && 'group-hover/media:bg-current/15',
      )}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className={cn('truncate font-medium underline-offset-2', canOpen && 'group-hover/media:underline')} title={title}>
          {title}
        </span>
        {subtitle && <span className="truncate text-[10px] opacity-60">{subtitle}</span>}
      </span>
    </>
  );

  return (
    <div
      data-testid="chat-media-link-card"
      className={cn('flex items-center gap-2.5 rounded-xl bg-current/8 p-2 text-xs min-w-[11rem]', canDownload && 'pr-1.5')}
    >
      {canOpen ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Open in new tab"
          className="group/media flex min-w-0 flex-1 items-center gap-2.5"
        >
          {body}
        </a>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2.5">{body}</div>
      )}
      {canDownload && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          title="Download"
          aria-label={`Download ${label.toLowerCase()}`}
          className="shrink-0 rounded-md p-1.5 transition-colors hover:bg-current/10 disabled:opacity-60"
        >
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        </button>
      )}
    </div>
  );
}

/** Single attachment renderer with type-aware display */
function SingleAttachment({
  attachment,
  isOutbound,
}: {
  attachment: MessageAttachment;
  isOutbound: boolean;
}) {
  const [mediaError, setMediaError] = useState(false);
  const [showMediaAuthHelp, setShowMediaAuthHelp] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // A cached image finishes decoding before React attaches `onLoad`, so that
  // handler never fires and the placeholder would sit there forever with the
  // real image hidden behind it. Ask the element as it mounts instead.
  const imgRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) setImgLoaded(true);
  }, []);

  const handleMediaError = useCallback(() => {
    setMediaError(true);
    setShowMediaAuthHelp(true);
  }, []);

  // Settings › Chat View › Media Display = "Download Link Only": image /
  // video / audio render as a compact open-or-download card instead of an
  // inline player. Documents already are a card, so they're unaffected.
  const linkOnly = useSettingsStore((s) => s.preferences.mediaDisplayMode === 'link');
  if (linkOnly && (attachment.type === 'image' || attachment.type === 'video' || attachment.type === 'audio')) {
    return <MediaLinkCard attachment={attachment} kind={attachment.type} isOutbound={isOutbound} />;
  }

  // Image
  if (attachment.type === 'image' && !mediaError) {
    // Hold a box while the image downloads so the bubble never paints text-only
    // and then jump. Uploads that recorded the intrinsic size get their exact
    // geometry; everything older — notes stored before dimensions were captured,
    // and provider media — falls back to the same h-40 w-56 skeleton the
    // unresolved-media placeholder uses, then relaxes to the natural layout once
    // the image decodes.
    const hasDims = !!(attachment.width && attachment.height);
    const isBoxed = hasDims || !imgLoaded;
    return (
      <>
        <div className="rounded-lg overflow-hidden relative group">
          <div
            className={cn(
              'relative',
              !hasDims && !imgLoaded && 'h-40 w-56',
              !imgLoaded && 'animate-pulse bg-muted rounded-lg'
            )}
            style={hasDims ? { aspectRatio: `${attachment.width} / ${attachment.height}`, maxHeight: '16rem' } : undefined}
          >
            <img
              ref={imgRef}
              src={attachment.url}
              alt="Media attachment"
              className={cn(
                'rounded-lg cursor-pointer hover:opacity-90 transition-opacity',
                isBoxed
                  ? 'absolute inset-0 h-full w-full object-cover'
                  : 'min-w-full max-h-64 object-cover',
                !imgLoaded && 'opacity-0'
              )}
              onLoad={() => setImgLoaded(true)}
              onError={handleMediaError}
              onClick={() => setLightboxOpen(true)}
            />
            {!imgLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
                <ImageIcon className="h-6 w-6 opacity-40" />
                {!!attachment.size && (
                  <span className="text-[10px] opacity-70">{formatFileSize(attachment.size)}</span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute top-1.5 right-1.5 rounded-md bg-black/40 hover:bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Expand"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {lightboxOpen && (
          <MediaLightbox
            url={attachment.url}
            type="image"
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </>
    );
  }

  // Video — inline player with expand
  // onError on <video> fires when the resource fails to load (including 401/403 from Twilio auth).
  // The <source> child also fires onError, which bubbles up — handled at the <video> level.
  if (attachment.type === 'video' && !mediaError) {
    return (
      <>
        <div className="rounded-lg overflow-hidden relative group">
          <video
            controls
            className="max-w-full max-h-64 rounded-lg"
            preload="metadata"
            playsInline
            onError={handleMediaError}
          >
            <source
              src={attachment.url}
              type={attachment.contentType || 'video/mp4'}
              onError={handleMediaError}
            />
            Your browser does not support the video tag.
          </video>
          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute top-1.5 right-1.5 rounded-md bg-black/40 hover:bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Expand"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {lightboxOpen && (
          <MediaLightbox
            url={attachment.url}
            type="video"
            contentType={attachment.contentType}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </>
    );
  }

  // Audio
  if (attachment.type === 'audio' && !mediaError) {
    return (
      <div className="rounded-lg overflow-hidden">
        <div className={cn(
          'flex items-center gap-2 p-2.5 text-xs',
          isOutbound ? 'bg-primary-foreground/10' : 'bg-background/50'
        )}>
          <Music className="h-4 w-4 shrink-0" />
          <span>Audio message</span>
        </div>
        <audio
          controls
          className="w-full mt-1"
          preload="metadata"
          onError={handleMediaError}
        >
          <source
            src={attachment.url}
            type={attachment.contentType || 'audio/mpeg'}
            onError={handleMediaError}
          />
        </audio>
      </div>
    );
  }

  // Media error (image / video / audio) → Twilio-aware warning, else generic
  if (mediaError && showMediaAuthHelp) {
    const isTwilioMedia = !!attachment.url && /(?:^|\.)twilio\.com\//i.test(attachment.url);
    // Type-aware labels so the wording fits the failed media kind
    const mediaKindLabel =
      attachment.type === 'video' ? 'video' :
      attachment.type === 'audio' ? 'audio' :
      'image';
    const actionVerb = attachment.type === 'image' ? 'display' : 'play';
    const failedHeader =
      attachment.type === 'video' ? "Video can't be played" :
      attachment.type === 'audio' ? "Audio can't be played" :
      "Image can't be displayed";

    // Twilio-specific: auth config issue — amber warning styling, security icon, numbered steps.
    // Uses amber palette in both inbound/outbound so it stays readable inside the primary-colored
    // outbound bubble (which otherwise forces text-primary-foreground → white-on-amber).
    if (isTwilioMedia) {
      return (
        <div className="mb-1 rounded-lg overflow-hidden text-xs border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/60">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 font-semibold bg-amber-200/80 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
            <span>Twilio is blocking this {mediaKindLabel}</span>
          </div>
          <div className="px-2.5 py-2 space-y-2 text-amber-950 dark:text-amber-50">
            <p className="leading-relaxed">
              Disable <b>Authentication for media access</b> in Twilio to {actionVerb} this {mediaKindLabel}.
            </p>
            <ol className="space-y-1.5">
              <li className="flex gap-2">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950">1</span>
                <span className="leading-relaxed break-all">
                  Go to{' '}
                  <a
                    href="https://console.twilio.com/us1/develop/sms/settings/general"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-amber-700 hover:text-amber-800 dark:text-amber-200 dark:hover:text-amber-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    https://console.twilio.com/us1/develop/sms/settings/general
                  </a>
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950">2</span>
                <span className="leading-relaxed">
                  Find <b>&ldquo;HTTP Basic Authentication for media access&rdquo;</b> and disable it.
                </span>
              </li>
            </ol>
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-md font-medium bg-amber-500 hover:bg-amber-600 text-white shadow-sm transition-colors dark:bg-amber-400 dark:hover:bg-amber-300 dark:text-amber-950"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
              <span>Open media directly</span>
            </a>
          </div>
        </div>
      );
    }

    // Generic media error — amber warning
    return (
      <div className={cn(
        'mb-1 rounded-lg p-2.5 text-xs space-y-1.5',
        isOutbound ? 'bg-primary-foreground/10' : 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800'
      )}>
        <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>{failedHeader}</span>
        </div>
        <p className="text-muted-foreground leading-relaxed">
          The media could not be loaded. It may require authentication or the URL may have expired.
        </p>
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="h-3 w-3" />
          <span>Open media directly</span>
        </a>
      </div>
    );
  }

  // Generic file — filename from the URL, fall back to a type label
  const typeLabel = attachment.contentType
    ? `${attachment.contentType.split('/')[1]?.toUpperCase() || 'File'} attachment`
    : 'File attachment';
  const displayName = attachmentFileNameFromUrl(attachment.url) || typeLabel;

  return (
    <div className={cn(
      'flex items-center gap-2 rounded-lg p-2.5 text-xs',
      isOutbound ? 'bg-primary-foreground/10' : 'bg-background/50'
    )}>
      <FileText className="h-4 w-4 shrink-0" />
      <span className="truncate" title={displayName}>{displayName}</span>
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="ml-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="h-3.5 w-3.5 opacity-60 hover:opacity-100 transition-opacity" />
      </a>
    </div>
  );
}

// --- Author avatar (notes + outbound messages) ---
function NoteAuthorAvatar({ author, size = 'md', variant = 'note' }: { author?: { name: string; email?: string; pic?: string; type?: string }; size?: 'sm' | 'md'; variant?: 'note' | 'outbound' }) {
  if (!author) return null;

  const initials = (author.name || author.email || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const sizeClasses = size === 'sm' ? 'h-4 w-4 text-[7px]' : 'h-6 w-6 text-[9px]';

  // Chat automation / workflow replies aren't from a person — show a Bot icon
  // rather than initials so it's obvious the customer was answered by a bot.
  if (author.type === 'uaapp-workflow') {
    return (
      <div
        title={author.name || 'Bot'}
        className={cn(sizeClasses, 'rounded-full flex items-center justify-center shrink-0 bg-violet-100 text-violet-600 dark:bg-violet-900/60 dark:text-violet-300')}
      >
        <Bot className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} />
      </div>
    );
  }

  if (author.pic) {
    return (
      <img
        src={author.pic}
        alt={author.name}
        title={author.email || author.name}
        className={cn(sizeClasses, 'rounded-full object-cover shrink-0')}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }

  const colorClasses = variant === 'outbound'
    ? 'bg-primary-foreground/20 text-primary-foreground/80'
    : 'bg-[#c4650f]/20 dark:bg-[#e1a46d]/20 text-[#c4650f] dark:text-[#e1a46d]';

  return (
    <div
      title={author.email || author.name}
      className={cn(sizeClasses, 'rounded-full flex items-center justify-center font-bold shrink-0', colorClasses)}
    >
      {initials}
    </div>
  );
}

// --- Call event bubble (sided, not centered) ---
function CallEventBubble({ id, message, showTimestamp, previousMessage }: { id?: string; message: Message; showTimestamp?: boolean; previousMessage?: Message }) {
  const callEventType = message.metadata?.callEventType as string;
  const callDir = message.metadata?.callDirection as string;
  const isInbound = callDir === 'inbound';
  const isOutbound = !isInbound;
  const isMissed = callEventType === 'missed' || callEventType === 'failed' || callEventType === 'rejected';
  const isCompleted = callEventType === 'completed';
  const isOngoing = callEventType === 'ringing' || callEventType === 'accepted';

  const author = (message as ConvertedMessage).author;
  const showAuthor = (() => {
    if (!author?.name) return false;
    const prevAuthor = (previousMessage as ConvertedMessage | undefined)?.author;
    if (
      previousMessage?.direction === message.direction &&
      !previousMessage.isSystemMessage &&
      prevAuthor
    ) {
      // Fall back to email/name when uid is absent (Twilio lazy-loaded authors).
      const sameAuthor =
        (prevAuthor.uid && author.uid && prevAuthor.uid === author.uid) ||
        (prevAuthor.email && author.email && prevAuthor.email === author.email) ||
        (prevAuthor.name && author.name && prevAuthor.name === author.name);
      if (sameAuthor) return false;
    }
    return true;
  })();

  let CallIcon = isInbound ? PhoneIncoming : PhoneOutgoing;
  let iconColor = 'text-emerald-500';

  if (isMissed) {
    CallIcon = isInbound ? PhoneMissed : PhoneOff;
    iconColor = 'text-red-500';
  } else if (isOngoing) {
    CallIcon = Phone;
    iconColor = 'text-blue-500';
  } else if (!isCompleted) {
    iconColor = 'text-blue-500';
  }

  return (
    <div
      id={id}
      className={cn('flex w-full py-0.5 group/msg', isOutbound ? 'justify-end' : 'justify-start')}
    >
      <div className={cn('flex flex-col', isOutbound ? 'items-end' : 'items-start')}>
        {/* Author name */}
        {showAuthor && author && (
          <span className="text-[11px] text-muted-foreground/70 font-medium mb-0.5 mx-1 truncate max-w-[200px]">
            {author.name}{author.uid === getEffectiveUid() ? ' (You)' : ''}
          </span>
        )}
        <div className={cn(
          'flex gap-1.5',
          isOutbound ? 'flex-row-reverse items-start' : 'flex-row items-center'
        )}>
          {/* Author avatar — first in DOM, rendered on right via flex-row-reverse */}
          {isOutbound && author && (
            showAuthor
              ? <div className="pt-0.5 shrink-0"><NoteAuthorAvatar author={author} size="sm" variant="note" /></div>
              : <div className="w-4 shrink-0" />
          )}
          <div
            className={cn(
              'inline-flex items-center gap-2.5 rounded-2xl px-4 py-2.5 text-[13px]',
              isOutbound
                ? 'bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-br-md'
                : 'bg-card text-foreground rounded-bl-md border border-border/50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
            )}
          >
            <div className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
              isOutbound
                ? 'bg-primary-foreground/15'
                : isMissed
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : isCompleted
                    ? 'bg-emerald-100 dark:bg-emerald-900/30'
                    : 'bg-blue-100 dark:bg-blue-900/30'
            )}>
              <CallIcon className={cn('h-4 w-4', isOutbound ? 'text-primary-foreground/80' : iconColor)} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-[13px] font-medium">WhatsApp Call</span>
              <div className="flex items-center gap-1.5">
                <span className={cn(
                  'text-[11px]',
                  isOutbound
                    ? 'text-primary-foreground/70'
                    : isMissed
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-muted-foreground'
                )}>
                  {message.body}
                </span>
                {showTimestamp && (
                  <span className={cn(
                    'text-[10px]',
                    isOutbound ? 'text-primary-foreground/40' : 'text-muted-foreground/40'
                  )}>
                    <MessageTime timestamp={message.timestamp} />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Resolve an agent email to a display name + photo. Reads the app-users
 * cache synchronously and triggers a background fetch on cache miss so the
 * system-message pill upgrades from a bare email → name + avatar once the
 * Cloud Function resolves. Falls back to the email's local part meanwhile.
 */
function useResolvedAgent(email?: string): { name: string; email: string; pic?: string } | null {
  const cached = useAppUsersStore((s) => (email ? s.userDetailsCache[email] : undefined));
  const fetchUserDetails = useAppUsersStore((s) => s.fetchUserDetails);

  const isUnassigned = email === 'unassigned' || email === 'unassign';

  useEffect(() => {
    if (email && !isUnassigned && !cached) {
      fetchUserDetails(email).catch(() => {});
    }
  }, [email, isUnassigned, cached, fetchUserDetails]);

  if (!email || isUnassigned) return null;
  return {
    name: cached?.name || email.split('@')[0],
    email,
    pic: cached?.photoURL,
  };
}

// --- System message (assignee-update, label-update, subject-update) ---
function SystemMessage({ id, message, showTimestamp }: { id?: string; message: Message; showTimestamp?: boolean }) {
  const sysType = message.noteType;
  const performer = message.metadata?.performer as string | undefined;
  const assigneeEmail = message.metadata?.assigneeEmail as string | undefined;
  const label = message.metadata?.label as string | undefined;
  const subject = message.metadata?.subject as string | undefined;

  // Build display text
  let displayText: React.ReactNode = message.body || 'System update';
  // Set by the chat-reminder branch: status-tinted Bell / BellOff / CheckCircle2.
  let reminderIcon: React.ReactNode = null;

  // Resolve performer + assignee emails to name + photo. Hooks run
  // unconditionally; the assignee lookup is skipped (undefined email) when
  // the chat is being unassigned or assigned to the performer themselves.
  // An opt-out row's performer is the literal "customer", not an agent email.
  const performerAgent = useResolvedAgent(sysType === 'optout-update' ? undefined : performer);
  const isAssigneeUnassigned = assigneeEmail === 'unassigned' || assigneeEmail === 'unassign';
  const isAssigneeSelf = !!assigneeEmail && assigneeEmail === performer;
  const assigneeAgent = useResolvedAgent(
    isAssigneeUnassigned || isAssigneeSelf ? undefined : assigneeEmail,
  );

  const performerChip = performerAgent ? (
    <span className="inline-flex items-center gap-1">
      <NoteAuthorAvatar
        author={{ name: performerAgent.name, email: performerAgent.email, pic: performerAgent.pic }}
        size="sm"
        variant="note"
      />
      <span className="font-semibold">{performerAgent.name}</span>
    </span>
  ) : null;

  if (sysType === 'assignee-update' && performerAgent) {
    const assigneeChip = isAssigneeUnassigned ? (
      <span className="font-semibold">Unassigned</span>
    ) : isAssigneeSelf ? (
      <span className="font-semibold">self</span>
    ) : assigneeAgent ? (
      <span className="inline-flex items-center gap-1">
        <NoteAuthorAvatar
          author={{ name: assigneeAgent.name, email: assigneeAgent.email, pic: assigneeAgent.pic }}
          size="sm"
          variant="note"
        />
        <span className="font-semibold">{assigneeAgent.name}</span>
      </span>
    ) : null;
    displayText = (
      <>
        {performerChip}
        {' assigned chat to '}
        {assigneeChip}
      </>
    );
  } else if (sysType === 'label-update' && performerAgent) {
    displayText = (
      <>
        {performerChip}
        {' changed status to '}
        <span className="font-semibold">{label || ''}</span>
      </>
    );
  } else if (sysType === 'subject-update' && performerAgent) {
    displayText = (
      <>
        {performerChip}
        {' changed subject to '}
        <span className="font-semibold">&ldquo;{subject || ''}&rdquo;</span>
      </>
    );
  } else if (sysType === 'chat-reminder') {
    const reminderSubject = (message.metadata?.reminderSubject as string) || subject || message.body || '';
    const reminderPerformAt = message.metadata?.reminderPerformAt as number | undefined;
    // The server writes one system doc per reminder ACTION — set, reschedule,
    // cancel, complete — all type 'chat-reminder', distinguished only by
    // status/isRescheduled. Rendering them all as "set a reminder" made a
    // cancel look like the reminder came back.
    const reminderStatus = message.metadata?.reminderStatus as string | undefined;
    const reminderVerb =
      reminderStatus === 'cancelled' ? ' cancelled the reminder'
      : reminderStatus === 'completed' ? ' completed the reminder'
      : message.metadata?.reminderIsRescheduled ? ' rescheduled the reminder'
      : ' set a reminder';
    // Tint only the verb and icon — the name/subject/due stay neutral so the
    // timeline doesn't turn into a rainbow. Docs from before `status` existed
    // fall through to amber, i.e. exactly the old rendering.
    const reminderTint =
      reminderStatus === 'cancelled' ? 'text-red-500 dark:text-red-400'
      : reminderStatus === 'completed' ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-amber-500 dark:text-amber-400';
    reminderIcon =
      reminderStatus === 'cancelled' ? <BellOff className={cn('h-3 w-3 shrink-0', reminderTint)} data-reminder-icon="cancelled" />
      : reminderStatus === 'completed' ? <CheckCircle2 className={cn('h-3 w-3 shrink-0', reminderTint)} data-reminder-icon="completed" />
      : <Bell className={cn('h-3 w-3 shrink-0', reminderTint)} data-reminder-icon="scheduled" />;
    // Automation-set reminders have no performer email — credit the flow by
    // name, and say who the reminder is FOR when the doc carries it.
    const reminderAuthorName = message.metadata?.reminderAuthorName as string | undefined;
    const reminderFor = message.metadata?.reminderFor as { name?: string; email?: string } | undefined;
    const reminderForLabel = reminderFor?.name || reminderFor?.email;
    displayText = (
      <span className="flex flex-col items-center gap-0.5">
        <span className="flex items-center gap-1">
          {performerChip || <span className="font-semibold">{reminderAuthorName || 'Someone'}</span>}
          <span className={reminderTint}>{reminderVerb}</span>
          {reminderForLabel && (
            <>
              {' for '}
              <span className="font-semibold">{reminderForLabel}</span>
            </>
          )}
        </span>
        {(reminderSubject || reminderPerformAt) && (
          <span className="flex items-center gap-1.5 text-[11px] opacity-70">
            {reminderSubject && <span>&ldquo;{reminderSubject}&rdquo;</span>}
            {reminderSubject && reminderPerformAt && <span>·</span>}
            {reminderPerformAt && (
              // A cancelled reminder's due time is void — don't let it read
              // as still pending.
              <span className={reminderStatus === 'cancelled' ? 'line-through' : undefined}>
                Due {formatRelativeTime(reminderPerformAt)}
              </span>
            )}
          </span>
        )}
      </span>
    );
  } else if (sysType === 'optout-update') {
    // KAN-155: the customer sent a STOP / START keyword.
    const optedOut = message.metadata?.optOut === true;
    const optOutKeyword = message.metadata?.optOutKeyword as string | undefined;
    displayText = (
      <>
        <span className={cn('font-semibold', optedOut ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400')}>
          {optedOut ? 'Customer opted out' : 'Customer opted in'}
        </span>
        {optOutKeyword && <span>{` (replied ${optOutKeyword})`}</span>}
      </>
    );
  }

  // Call permission messages get a distinct style
  const isCallPermission = !!(message.metadata?.isCallPermissionReply || message.metadata?.isCallPermissionRequest);
  const callPermData = message.metadata?.callPermissionData as Record<string, unknown> | undefined;
  const isPermAccepted = callPermData?.response === 'accept';
  const isPermRequest = !!message.metadata?.isCallPermissionRequest;

  if (isCallPermission) {
    const bgClass = isPermRequest
      ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40'
      : isPermAccepted
        ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40'
        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40';
    const iconColor = isPermRequest
      ? 'text-blue-500'
      : isPermAccepted ? 'text-emerald-500' : 'text-red-500';
    const CallIcon = isPermRequest ? Phone : isPermAccepted ? Phone : PhoneOff;

    return (
      <div id={id} className="flex flex-col w-full items-center py-1.5 group/msg">
        <div className={cn('inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[12px] text-foreground/80', bgClass)}>
          <CallIcon className={cn('h-3.5 w-3.5 shrink-0', iconColor)} />
          <span>{message.body}</span>
          {showTimestamp && (
            <span className="text-[10px] opacity-50 ml-1">
              <MessageTime timestamp={message.timestamp} />
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div id={id} className="flex flex-col w-full items-center py-1 group/msg">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-[#d8fcff] dark:bg-[#bbccce]/20 px-5 py-1.5 text-[12px] text-neutral-500 dark:text-gray-300">
        {sysType === 'assignee-update' && <UserCheck className="h-3 w-3 shrink-0 opacity-60" />}
        {sysType === 'label-update' && <Tag className="h-3 w-3 shrink-0 opacity-60" />}
        {sysType === 'chat-reminder' && reminderIcon}
        {sysType === 'optout-update' && (message.metadata?.optOut === true
          ? <Ban className="h-3 w-3 shrink-0 text-rose-500" />
          : <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />)}
        <div className="flex items-center gap-1">{displayText}</div>
        {showTimestamp && (
          <span className="text-[10px] opacity-50 ml-1">
            <MessageTime timestamp={message.timestamp} />
          </span>
        )}
      </div>
    </div>
  );
}

// --- Scheduled message indicator with cancel button ---
function ScheduledIndicator({
  message,
  onCancel,
}: {
  message: Message;
  onCancel?: (messageId: string) => Promise<{ success: boolean; error?: string }>;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const handleCancel = useCallback(async () => {
    if (!onCancel || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    const result = await onCancel(message.id);
    if (!result.success) {
      setCancelError(result.error || 'Failed to cancel');
    }
    setCancelling(false);
  }, [onCancel, message.id, cancelling]);

  return (
    <div className="mt-1.5 pt-1.5 border-t border-primary/15 dark:border-primary/20 flex items-center gap-1.5 text-[11px] text-primary/60 dark:text-primary/70">
      <CalendarClock className="h-3 w-3 shrink-0" />
      <span className="font-medium flex-1">
        Scheduled{message.metadata?.scheduledAt
          ? ` for ${formatScheduleDate(new Date(message.metadata.scheduledAt as string))}`
          : ''}
      </span>
      {onCancel && (
        <button
          onClick={handleCancel}
          disabled={cancelling}
          className="flex items-center gap-0.5 font-medium text-primary/70 dark:text-primary/80 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
          title="Cancel scheduled message"
        >
          {cancelling ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Ban className="h-3 w-3" />
          )}
          <span>{cancelling ? 'Cancelling...' : 'Cancel'}</span>
        </button>
      )}
      {cancelError && (
        <span className="text-red-500 text-[10px]">{cancelError}</span>
      )}
    </div>
  );
}

// --- Date separator ---
interface DateSeparatorProps {
  date: string;
}

export function DateSeparator({ date }: DateSeparatorProps) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1 border-t border-border/50" />
      <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
        {date}
      </span>
      <div className="flex-1 border-t border-border/50" />
    </div>
  );
}
